import { findModel as findCatalogueModel } from '../data/catalogue';
import { findClearance } from '../data/clearance';
import {
  PRICES, STOCK, RATES, GST_RATE, KIT_COPPER_METRES, KIT_ADDON, DELIVERY_CHARGE, EXCHANGE,
  OPTIONAL_ADDONS, MANDATORY_ADDONS, PAYMENT, BUSINESS,
} from '../data/pricing';


// GST convention, deliberate:
//   Equipment and the kit add-on are GST INCLUSIVE, as you quote them.
//   Installation and materials are EX GST, as your rate card reads.
// They are kept as separate subtotals with GST on the work portion shown as
// its own line, so the customer sees the ex-GST rates they expect and still
// sees a true final number.
//
// Delivery is never a figure here. It is quoted at actuals and every total
// says so rather than quietly omitting it.

const inr = (n) => Math.round(n).toLocaleString('en-IN');
export const formatINR = (n) =>
  typeof n === 'number' && !Number.isNaN(n) ? `\u20B9${inr(n)}` : 'On request';

export function newQuoteRef(date = new Date()) {
  const y = String(date.getFullYear()).slice(2);
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `CC-${y}${m}${d}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
}

// A cart line's model: a Daikin catalogue model, or an old-stock clearance
// unit (data/clearance.js). Both are priced from PRICES and stocked from STOCK
// under their own id, so cart, checkout and the payment server sell them alike.
const findModel = (id) => findCatalogueModel(id) || findClearance(id);

export const priceOf = (modelId, kit) => PRICES[modelId]?.[kit] ?? null;
export const variantInStock = (modelId, kit) => (STOCK[modelId]?.[kit] || 0) > 0;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

// Old-unit value by tonnage and coil. Bands are inclusive of their maxTon,
// so a 1.5 ton unit takes the 1.5 band, not the 2-and-above one.
export function exchangeValue(ton, coil) {
  if (!EXCHANGE.enabled) return 0;
  if (coil === 'aluminium') return EXCHANGE.aluminium;
  if (coil !== 'copper') return 0;
  const t = Number(ton);
  if (!t) return 0;
  const band = EXCHANGE.copper.find((b) => t <= b.maxTon);
  return band ? band.value : 0;
}

export function priceLine(line) {
  const model = findModel(line.modelId);
  if (!model) return null;

  const kit = model.variants[line.kit] ? line.kit : (model.variants.withoutKit ? 'withoutKit' : 'withKit');
  const unitPrice = priceOf(model.id, kit);
  if (unitPrice == null) return null;

  const qty = Math.max(1, Number(line.qty) || 1);
  // Opt-in, matching newLine() in cart.js. Defaulting to true meant any cart
  // saved before this field existed would silently be charged for
  // installation the customer never asked for.
  const withInstall = line.withInstall === true;

  // Kit add-on only applies to a without-kit unit, and only while you have kits.
  const kitAddon = Boolean(line.kitAddon) && kit === 'withoutKit' && KIT_ADDON.available;
  const kitAddonAmount = kitAddon ? KIT_ADDON.price * qty : 0;

  const equipment = unitPrice * qty + kitAddonAmount;   // GST inclusive
  const mrp = (model.variants[kit]?.mrp ?? 0) * qty;

  // Copper covered by a kit, whether it came in the box or was added on.
  const copperCovered = (kit === 'withKit' || kitAddon) ? KIT_COPPER_METRES : 0;
  const copperM = num(line.copperM);
  const chargeableCopper = Math.max(0, copperM - copperCovered);
  const drainM = num(line.drainM);
  const wireM = num(line.wireM);

  const work = [];
  if (withInstall) {
    work.push({ key: 'installSplit', label: RATES.installSplit.label, amount: RATES.installSplit.price * qty });

    MANDATORY_ADDONS.forEach((k) => {
      work.push({ key: k, label: RATES[k].label, amount: RATES[k].price * qty, mandatory: true });
    });

    // Three runs, three lengths. Each billed only if the customer gave a figure.
    if (chargeableCopper > 0) {
      work.push({
        key: 'copperSplit',
        label: `${RATES.copperSplit.label}, ${chargeableCopper} m`,
        amount: RATES.copperSplit.price * chargeableCopper * qty,
      });
    }
    if (drainM > 0) {
      work.push({
        key: 'drainPipe',
        label: `${RATES.drainPipe.label}, ${drainM} m`,
        amount: RATES.drainPipe.price * drainM * qty,
      });
    }
    if (wireM > 0) {
      work.push({
        key: 'controlWiring',
        label: `${RATES.controlWiring.label}, ${wireM} m`,
        amount: RATES.controlWiring.price * wireM * qty,
      });
    }

    OPTIONAL_ADDONS.forEach((k) => {
      if (!line[k]) return;
      // The engineer removes the old unit anyway when it is being exchanged,
      // so dismantling is not a separate charge.
      if (k === 'dismantleOld' && line.exchange) return;
      const rateKey = k === 'standSplit15' && model.kw <= 3.6 ? 'standSplit1' : k;
      work.push({ key: rateKey, label: RATES[rateKey].label, amount: RATES[rateKey].price * qty });
    });
  }

  // Installation is surveyed and quoted separately, so it is never priced
  // at checkout. withInstall still travels on the line: it is the yes/no
  // that tells you to call and survey.
  const workExGst = 0;

  // One old unit taken per new unit bought, so the allowance scales with qty.
  const exchange = Boolean(line.exchange) && EXCHANGE.enabled;
  const exchangeAmount = exchange
    ? exchangeValue(line.exchangeTon, line.exchangeCoil) * qty
    : 0;

  return {
    exchange,
    exchangeAmount,
    exchangeTon: line.exchangeTon || '',
    exchangeCoil: line.exchangeCoil || '',
    lineId: line.lineId,
    model, kit, qty, unitPrice, equipment, mrp, withInstall,
    kitAddon, kitAddonAmount,
    copperM, drainM, wireM, chargeableCopper, copperCovered,
    work: [], workExGst,
    savings: Math.max(0, mrp - unitPrice * qty),
    measurementsGiven: copperM > 0 || drainM > 0 || wireM > 0,
  };
}

// Delivery is passed in from the pincode check at checkout. It is a separate
// all-inclusive line — GST is already inside the figure — so it sits outside
// the installation block rather than being taxed alongside it.
export function priceQuote(lines = [], { delivery = null } = {}) {
  const priced = lines.map(priceLine).filter(Boolean);
  const equipment = priced.reduce((s, l) => s + l.equipment, 0);
  const workExGst = priced.reduce((s, l) => s + l.workExGst, 0);
  const workGst = Math.round(workExGst * GST_RATE);
  const deliveryAmount = delivery && !delivery.free ? delivery.price : 0;
  // Deducted after GST: it is money off the bill, not a taxable supply.
  const exchangeTotal = priced.reduce((s, l) => s + l.exchangeAmount, 0);

  return {
    lines: priced,
    units: priced.reduce((s, l) => s + l.qty, 0),
    equipment,
    workExGst,
    workGst,
    workTotal: workExGst + workGst,
    delivery,
    deliveryAmount,
    deliveryKnown: Boolean(delivery),
    exchangeTotal,
    subtotal: equipment + workExGst + workGst,
    total: Math.max(0, equipment + workExGst + workGst + deliveryAmount - exchangeTotal),
    savings: priced.reduce((s, l) => s + l.savings, 0),
    anyInstall: priced.some((l) => l.withInstall),
    allMeasured: priced.every((l) => !l.withInstall || l.measurementsGiven),
  };
}

// --- WhatsApp handoff -------------------------------------------------------

export function quoteToWhatsAppText({ ref, quote, customer = {} }) {
  const out = [`Hi, I'd like to confirm this quote from citicool.in`, '', `Reference: ${ref}`];
  if (customer.name) out.push(`Name: ${customer.name}`);
  if (customer.area) out.push(`Location: ${customer.area}`);
  out.push('');

  quote.lines.forEach((l, i) => {
    out.push(`${i + 1}. ${l.model.title}`);
    out.push(`   ${l.model.variants[l.kit].idu} · qty ${l.qty} · ${l.kit === 'withKit' ? 'with kit' : 'without kit'}`);
    if (l.kitAddon) out.push(`   + ${KIT_ADDON.label}`);
    out.push(`   ${formatINR(l.unitPrice)} each, GST included`);
    if (!l.withInstall) {
      out.push(`   Unit only, no installation`);
    } else {
      l.work.forEach((w) => out.push(`   + ${w.label} — ${formatINR(w.amount)}`));
      if (!l.measurementsGiven) out.push(`   Piping measured on site`);
    }
  });

  out.push('');
  out.push(`Equipment (GST incl): ${formatINR(quote.equipment)}`);
  if (quote.workExGst > 0) {
    out.push(`Installation and materials: ${formatINR(quote.workExGst)} + GST ${formatINR(quote.workGst)}`);
  }
  if (quote.delivery) {
    out.push(`Delivery (${quote.delivery.zone}, GST incl): ${quote.delivery.free ? 'Free' : formatINR(quote.deliveryAmount)}`);
  }
  out.push(`Total: ${formatINR(quote.total)}`);
  if (quote.savings > 0) out.push(`Saving against MRP: ${formatINR(quote.savings)}`);
  if (customer.notes) { out.push(''); out.push(`Notes: ${customer.notes}`); }
  return out.join('\n');
}

export const whatsAppLink = (text) =>
  `https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(text)}`;

export const quoteWhatsAppLink = (payload) => whatsAppLink(quoteToWhatsAppText(payload));

export function modelWhatsAppLink(model, kit = 'withKit') {
  const v = model.variants[kit];
  const p = priceOf(model.id, kit);
  return whatsAppLink([
    `Hi, I'm interested in this from citicool.in`,
    '',
    model.title,
    `${v.idu} + ${v.odu}`,
    `${model.kw} kW · ISEER ${model.iseer} · ${kit === 'withKit' ? 'with piping kit' : 'without kit'}`,
    p ? `Listed at ${formatINR(p)} including GST` : '',
    '',
    `Please confirm availability and installation for my location.`,
  ].filter(Boolean).join('\n'));
}

export { PAYMENT, DELIVERY_CHARGE, KIT_ADDON };